import React, {useEffect, useState} from 'react';
import {
    App,
    Button,
    Col,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Row,
    Space,
    Switch,
    Table,
    Tag,
    Typography,
} from 'antd';
import {
    DeleteOutlined,
    EditOutlined,
    ExclamationCircleOutlined,
    PlusOutlined,
    ReloadOutlined,
    TagsOutlined,
} from '@ant-design/icons';
import type {ColumnsType} from 'antd/es/table';
import {MemoryBlockItem, memoryService} from '../../services/memoryService';

const {Title} = Typography;

/**
 * 共享记忆块管理页：管理员创建/维护共享记忆块（按类型区分，可配置到智能体记忆体）
 */
const MemoryBlockManage: React.FC = () => {
    const {message: messageApi} = App.useApp();
    const [blocks, setBlocks] = useState<MemoryBlockItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingBlock, setEditingBlock] = useState<MemoryBlockItem | null>(null);
    const [form] = Form.useForm();

    const loadBlocks = async () => {
        setLoading(true);
        try {
            const data = await memoryService.listBlocks();
            setBlocks(data || []);
        } catch {
            messageApi.error('加载共享记忆块失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadBlocks();
    }, []);

    const handleAdd = () => {
        setEditingBlock(null);
        form.resetFields();
        form.setFieldsValue({sort: 0, status: 1});
        setModalVisible(true);
    };

    const handleEdit = (record: MemoryBlockItem) => {
        setEditingBlock(record);
        form.setFieldsValue({
            name: record.name,
            description: record.description,
            sort: record.sort ?? 0,
            status: record.status === 1,
        });
        setModalVisible(true);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            const payload = {
                name: values.name,
                description: values.description,
                sort: values.sort ?? 0,
                status: values.status ? 1 : 0,
            };
            if (editingBlock && editingBlock.id) {
                await memoryService.updateBlock(editingBlock.id, payload);
                messageApi.success('更新成功');
            } else {
                await memoryService.createBlock(payload);
                messageApi.success('创建成功');
            }
            setModalVisible(false);
            await loadBlocks();
        } catch (e: any) {
            if (e?.errorFields) {
                return;
            }
            messageApi.error(e?.response?.data?.message || '保存失败');
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await memoryService.deleteBlock(id);
            messageApi.success('删除成功');
            await loadBlocks();
        } catch (e: any) {
            messageApi.error(e?.response?.data?.message || '删除失败');
        }
    };

    const columns: ColumnsType<MemoryBlockItem> = [
        {title: 'ID', dataIndex: 'id', key: 'id', width: 70},
        {
            title: '块名称', dataIndex: 'name', key: 'name', width: 200,
            render: (name: string) => <Tag color="geekblue">{name}</Tag>,
        },
        {title: '描述', dataIndex: 'description', key: 'description', ellipsis: true},
        {title: '排序', dataIndex: 'sort', key: 'sort', width: 80},
        {
            title: '状态', dataIndex: 'status', key: 'status', width: 100,
            render: (status: number) => status === 1
                ? <Tag color="green">启用</Tag>
                : <Tag>停用</Tag>,
        },
        {title: '创建时间', dataIndex: 'createdTime', key: 'createdTime', width: 170},
        {
            title: '操作', key: 'action', width: 140,
            render: (_, record) => (
                <Space size="small">
                    <Button type="text" icon={<EditOutlined/>} onClick={() => handleEdit(record)} size="small"/>
                    <Popconfirm title="确定删除该块？删除后块内记忆与全部绑定将一并清除"
                                onConfirm={() => handleDelete(record.id)}
                                icon={<ExclamationCircleOutlined style={{color: 'red'}}/>}>
                        <Button type="text" danger icon={<DeleteOutlined/>} size="small"/>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{padding: 24}}>
            <Row justify="space-between" align="middle" style={{marginBottom: 16}}>
                <Col>
                    <Title level={4} style={{margin: 0}}>
                        <TagsOutlined style={{marginRight: 8}}/>共享记忆块管理
                    </Title>
                </Col>
                <Col>
                    <Space>
                        <Button icon={<ReloadOutlined/>} onClick={loadBlocks} loading={loading}/>
                        <Button type="primary" icon={<PlusOutlined/>} onClick={handleAdd}>新增块</Button>
                    </Space>
                </Col>
            </Row>
            <Table columns={columns} dataSource={blocks} rowKey="id" loading={loading}
                   pagination={false} size="small"/>

            <Modal
                title={editingBlock ? '编辑共享记忆块' : '新增共享记忆块'}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={handleSave}
                width={520}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="块名称"
                        name="name"
                        rules={[{required: true, message: '请输入块名称'}]}
                    >
                        <Input placeholder="如：市场认知、交易偏好、复盘结论" maxLength={100}/>
                    </Form.Item>
                    <Form.Item label="描述" name="description">
                        <Input.TextArea placeholder="块用途说明（可选）" rows={3} maxLength={500}/>
                    </Form.Item>
                    <Form.Item label="排序值（越小越靠前）" name="sort">
                        <InputNumber style={{width: '100%'}}/>
                    </Form.Item>
                    <Form.Item label="启用" name="status" valuePropName="checked">
                        <Switch/>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default MemoryBlockManage;
